import type { ComponentProps } from 'react'

import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import ImageGalleryLightboxWithThumbnails from 'LuxKit/Carousel/ImageGalleryLightboxWithThumbnails'
import type { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import responsiveHelper from 'components/Common/Breakpoint/responsiveHelper'
import type { ModalContextType } from 'contexts/ModalContext'

type InheritedImageGalleryProps = Pick<
  ComponentProps<typeof ImageGallery>,
  'onImageChange' | 'topRight' | 'imageParams' | 'className' | 'onOpenLightbox' | 'disableLightbox'
>

interface Props extends InheritedImageGalleryProps {
  room: App.Property.Room | App.PackageRoomType | App.BedbankPackage
}

/**
 * Opens the same room-image lightbox `OfferPageRoomGallery` / `ImageGallery` use.
 * Breakpoint is read at click time so callers do not subscribe to resize.
 */
export function openOfferPageRoomGalleryLightbox(
  showLightbox: ModalContextType,
  images: Array<App.Image>,
  onImageChange?: ImageChangeHandler,
) {
  if (!images.length) return
  const LightboxComponent = responsiveHelper.matchesMin('desktop')
    ? ImageGalleryLightboxWithThumbnails
    : ImageGalleryLightbox
  return showLightbox(<LightboxComponent
    images={images}
    defaultIndex={0}
    onImageChange={onImageChange}
  />)
}

function OfferPageRoomGallery(props: Props) {
  const {
    room,
    ...rest
  } = props

  const isDesktop = useScreenSizeUp('desktop')
  const useThumbnailLightbox = isDesktop
  if (!room.images) return null

  return <ImageGallery
    {...rest}
    images={room.images}
    useThumbnailLightbox={useThumbnailLightbox}
    size="small"
    showImageTitles={false}
  />
}

export default OfferPageRoomGallery
